import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Card, CardFormData } from '../../types';

interface CardState {
  cards: Card[];
  currentCard: Card | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

const initialState: CardState = {
  cards: [],
  currentCard: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
};

export const fetchCards = createAsyncThunk(
  'cards/fetchAll',
  async ({ clubId, page = 1, pageSize = 50 }: { clubId?: string; page?: number; pageSize?: number }) => {
    const endpoint = clubId ? `/cards-club/${clubId}` : '/cards';
    const response = await api.get<Card[]>(endpoint, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  }
);

export const fetchCard = createAsyncThunk('cards/fetchOne', async (cardId: string) => {
  const response = await api.get<Card>(`/card/${cardId}`);
  return response.data;
});

export const createCard = createAsyncThunk(
  'cards/create',
  async ({ clubId, data }: { clubId: string; data: CardFormData }) => {
    const response = await api.post<Card>('/card', { ...data, club_id: clubId });
    return response.data;
  }
);

export const updateCard = createAsyncThunk(
  'cards/update',
  async ({ cardId, data }: { cardId: string; data: Partial<Card> }) => {
    const response = await api.put<Card>(`/card/${cardId}`, data);
    return response.data;
  }
);

export const deleteCard = createAsyncThunk('cards/delete', async (cardId: string) => {
  await api.delete(`/card/${cardId}`);
  return cardId;
});

export const generateCard = createAsyncThunk('cards/generate', async (cardId: string) => {
  const response = await api.post(`/generate-one-membership-card/${cardId}`);
  return response.data;
});

export const sendCardEmail = createAsyncThunk('cards/sendEmail', async (cardId: string) => {
  const response = await api.post(`/send-email-by-id/${cardId}`);
  return response.data;
});

export const toggleCardActive = createAsyncThunk('cards/toggleActive', async (cardId: string) => {
  const response = await api.post(`/toggle-card-activation/${cardId}`);
  return response.data;
});

export const updatePaymentStatus = createAsyncThunk(
  'cards/updatePayment',
  async ({ cardId, pagato }: { cardId: string; pagato: boolean }) => {
    const response = await api.post(`/update-pagato/${cardId}`, { pagato });
    return response.data;
  }
);

export const searchCards = createAsyncThunk(
  'cards/search',
  async ({ searchString, clubId }: { searchString: string; clubId?: string }) => {
    const endpoint = clubId
      ? `/search-cards-club/${clubId}/${searchString}`
      : `/search-cards/${searchString}`;
    const response = await api.get<Card[]>(endpoint);
    return response.data;
  }
);

const cardSlice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    setCurrentCard: (state, action: PayloadAction<Card | null>) => {
      state.currentCard = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cards
      .addCase(fetchCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.loading = false;
        state.cards = action.payload;
      })
      .addCase(fetchCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch cards';
      })
      // Fetch single card
      .addCase(fetchCard.fulfilled, (state, action) => {
        state.currentCard = action.payload;
      })
      // Create card
      .addCase(createCard.fulfilled, (state, action) => {
        state.cards.push(action.payload);
      })
      // Update card
      .addCase(updateCard.fulfilled, (state, action) => {
        const index = state.cards.findIndex((c) => c.record_id === action.payload.record_id);
        if (index !== -1) {
          state.cards[index] = action.payload;
        }
        if (state.currentCard?.record_id === action.payload.record_id) {
          state.currentCard = action.payload;
        }
      })
      // Delete card
      .addCase(deleteCard.fulfilled, (state, action) => {
        state.cards = state.cards.filter((c) => c.record_id !== action.payload);
        if (state.currentCard?.record_id === action.payload) {
          state.currentCard = null;
        }
      })
      // Search cards
      .addCase(searchCards.fulfilled, (state, action) => {
        state.cards = action.payload;
      });
  },
});

export const { setCurrentCard, setPage, clearError } = cardSlice.actions;
export default cardSlice.reducer;